import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks.js';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { PropertyTypeConflictError } from '@/lib/crm/core/errors';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { isValidEntityTypeName } from '@/lib/crm/core/types';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import { useSubmitEvents } from '@/lib/crm/query/mutations';
import { useCrmObject } from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import { getPropertyValue, unwrapPropertyValue } from '@/types/crm';

const AHOY_AGENT = 'ahoy_agent' as EntityTypeName;
const AHOY_USER = 'ahoy_user' as EntityTypeName;
const USER_TO_AGENT = 'ahoy_user_to_agent';

/** Ontology kind that marks a type as a pipeline the agent can be granted. */
const CASE_KIND = 'case';

/**
 * One case-kind entity type the agent can be granted, ready to render as a
 * row. `label` comes from the org's schema and is customer text — render it
 * exactly as it comes back, never recased.
 */
export interface AgentCaseType {
  entityTypeName: string;
  label: string;
}

/** A single pipeline entity type's per-action grant flags. */
export interface PipelineGrant {
  markCompleted: boolean;
  moveBackward: boolean;
  mergeDuplicates: boolean;
  assignToTeammates: boolean;
  editEnteredDetails: boolean;
}

export type PipelineGrantRowValue = { entityTypeName: string } & PipelineGrant;

/** A property value the agent entity accepts on the wire: select values and
 *  composite rows are strings only — the backend rejects booleans. */
export type AgentSettingValue =
  | string
  | string[]
  | Array<Record<string, string>>;

/**
 * The agent's ratified settings (docs/claude/AGENT_AUTONOMY.md), plus the
 * case-kind types the org has so the UI can render one grant row each.
 *
 * Pausing (`saveEnabled(false)`) and turning the contacts scope off write
 * only their own property: stored grants are PRESERVED and gated server-side
 * (`run_gates.py` skips every run while paused; `AgentSettings.parse` gates
 * the contacts extras on `ahoy_add_contacts`). A resume restores the user's
 * configuration — owner ruling 2026-09-12, superseding the earlier
 * pause-clears-all rule.
 */
export interface AgentSettings {
  enabled: boolean;
  addContacts: boolean;
  mergeContacts: boolean;
  assignContacts: boolean;
  editContacts: boolean;
  /** Per-pipeline-type grant flags, keyed by entity type name. A present key
   * IS the base grant. */
  pipelineGrants: Record<string, PipelineGrant>;
  /** Every case-kind type in the org's schema, sorted by label. */
  caseTypes: AgentCaseType[];
  isLoading: boolean;
  isAbsent: boolean;
  hasError: boolean;
  isSaving: boolean;
  saveEnabled(enabled: boolean): Promise<void>;
  saveAddContacts(addContacts: boolean): Promise<void>;
  saveMergeContacts(mergeContacts: boolean): Promise<void>;
  saveAssignContacts(assignContacts: boolean): Promise<void>;
  saveEditContacts(editContacts: boolean): Promise<void>;
  savePipelineGrants(rows: PipelineGrantRowValue[]): Promise<void>;
  /**
   * Write any set of agent properties in ONE `propertiesUpdated` event. This
   * is how the autonomy slider writes a whole matrix (scalars + the
   * `ahoy_pipeline_grants` composite) atomically. Retries once on a seq
   * collision using the server's `last_seq` — the agent entity's seq advances
   * under the page on every run (`ahoy_agent_ran` lands on it).
   */
  saveSettings(props: Record<string, AgentSettingValue>): Promise<void>;
  retry(): void;
}

/**
 * Read a `"true"` / `"false"` select property. Values arrive as strings from
 * the schema but tolerate booleans (optimistic writes, older rows).
 */
function toBooleanSetting(raw: unknown, fallback: boolean): boolean {
  const value = getPropertyValue<unknown>(raw);
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  return value !== 'false' && value !== false;
}

/**
 * Read a row-level `"true"` / `"false"` flag from a composite property row.
 * Fail-closed: anything other than a boolean `true` or the string `"true"`
 * (case/whitespace-insensitive) reads as false.
 */
function parseRowBoolean(raw: unknown): boolean {
  if (raw === true) {
    return true;
  }
  return String(raw).trim().toLowerCase() === 'true';
}

/**
 * Read the composite `ahoy_pipeline_grants` property into a per-entity-type
 * grant record. The wire shape varies (an array of wrappers, a wrapper around
 * an array, a bare single row, or a JSON-encoded string), so every form is
 * normalized here rather than at the call sites. Rows are defensively
 * unwrapped again — they're typically already flat plain objects (precedent:
 * `ahoy_invitations`/`ahoy_permissions`). Malformed rows are skipped;
 * duplicate entity type keys resolve last-write-wins.
 */
function toPipelineGrants(raw: unknown): Record<string, PipelineGrant> {
  let value = unwrapPropertyValue(raw);
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  let list: unknown[];
  if (Array.isArray(value)) {
    list = value;
  } else if (value == null) {
    list = [];
  } else {
    list = [value];
  }

  const grants: Record<string, PipelineGrant> = {};
  for (const row of list) {
    const r = unwrapPropertyValue(row);
    if (typeof r !== 'object' || r === null) {
      continue;
    }
    const entityTypeName = (r as Record<string, unknown>).entity_type_name;
    // Drop malformed rows AND any non-entity-type key. isValidEntityTypeName is
    // the canonical contract check: it rejects `*_timeline` projection
    // identifiers (reserved for internal use) and any string that is neither a
    // system `ahoy_*` type nor a custom `org_*` type. A stale `*_timeline` grant
    // that slipped in must not be parsed back into a grant here, or a later
    // pipeline save would resend it as an entity type and the whole write could
    // be rejected. Fail closed on anything the contract doesn't recognise.
    if (
      typeof entityTypeName !== 'string' ||
      !isValidEntityTypeName(entityTypeName)
    ) {
      continue;
    }
    const fields = r as Record<string, unknown>;
    grants[entityTypeName] = {
      markCompleted: parseRowBoolean(fields.mark_completed),
      moveBackward: parseRowBoolean(fields.move_backward),
      mergeDuplicates: parseRowBoolean(fields.merge_duplicates),
      assignToTeammates: parseRowBoolean(fields.assign_to_teammates),
      editEnteredDetails: parseRowBoolean(fields.edit_entered_details),
    };
  }
  return grants;
}

export function useAgentSettings(): AgentSettings {
  const user = useUser();
  const {
    entityId: userEntityId,
    orgId,
    isLoading: isUserBootstrapLoading,
  } = useCurrentUserEntity();
  const orgIdNum = orgId ? Number(orgId) : 0;

  // Resolve the current user's CRM object to find the agent association.
  // Must use includeAssociations: true — the entity batcher hardcodes it to
  // false, so snapshot hooks won't populate .associations for existing users.
  const userQuery = useCrmObject(
    orgIdNum,
    AHOY_USER,
    userEntityId ?? undefined,
    {
      includeAssociations: true,
    }
  );

  const agentEntityId = userQuery.object?.associations?.find(
    a => a.association_type_name === USER_TO_AGENT
  )?.target_entity_id;

  // Read the agent object via the store.
  const agent = useCrmObject(orgIdNum, AHOY_AGENT, agentEntityId ?? undefined);

  const { mutateAsync: submitEvents, isPending } = useSubmitEvents(
    orgIdNum,
    AHOY_AGENT
  );

  // Every case-kind type in the org's schema — one grant row each. `*_timeline`
  // types alias their base type and are never product surfaces, so they are
  // filtered the same way the nav filters them.
  const objectTypesQuery = useObjectTypes(orgIdNum || undefined);
  const allObjectTypes = objectTypesQuery.data?.allObjectTypes;
  const caseTypes = useMemo<AgentCaseType[]>(() => {
    if (!allObjectTypes) {
      return [];
    }
    return allObjectTypes
      .filter(
        t =>
          (t.entity_kind ?? []).includes(CASE_KIND) &&
          !t.entity_type_name.endsWith('_timeline')
      )
      .map(t => ({
        entityTypeName: t.entity_type_name,
        // Plural: a grant covers every record of the type, not one record.
        label: t.label_plural || t.label || t.entity_type_name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allObjectTypes]);

  // Derive property values.
  const props = agent.object?.properties;
  // Unset settings read as OFF — matching the backend's fail-closed
  // AgentSettings.parse. An unset ahoy_enabled means the agent takes no
  // action, so showing it as Active would be a lie: schema default_value does
  // NOT surface on property reads, and provisioning always materializes real
  // values, so unset only occurs for anomalous rows.
  const enabled = toBooleanSetting(props?.ahoy_enabled?.value, false);
  const addContacts = toBooleanSetting(props?.ahoy_add_contacts?.value, false);
  const mergeContacts = toBooleanSetting(
    props?.ahoy_merge_contacts?.value,
    false
  );
  const assignContacts = toBooleanSetting(
    props?.ahoy_assign_contacts?.value,
    false
  );
  const editContacts = toBooleanSetting(
    props?.ahoy_edit_contacts?.value,
    false
  );
  const pipelineGrants = useMemo(
    () => toPipelineGrants(props?.ahoy_pipeline_grants),
    [props?.ahoy_pipeline_grants]
  );

  // isLoading: waiting for the org/user bootstrap, OR waiting for the user
  // object, OR waiting for the agent object.
  const isLoading =
    isUserBootstrapLoading ||
    (userQuery.isLoading && !userQuery.object) ||
    (agentEntityId !== undefined && agent.isLoading && !agent.object);

  // isAbsent: bootstrap has resolved, user object loaded but no agent
  // association found, OR agent entity ID present but object never resolved
  // after fetch settled. Bootstrap must be excluded first — otherwise the
  // ordinary org/user resolution race on every page load reports "absent"
  // instead of "loading".
  const userObjectLoaded = !userQuery.isLoading || !!userQuery.object;
  const isAbsent =
    !isUserBootstrapLoading &&
    ((userObjectLoaded && agentEntityId === undefined) ||
      (agentEntityId !== undefined && !agent.isLoading && !agent.object));

  // hasError: a fetch of the user or agent object failed. Independent of
  // isAbsent — isAbsent means "resolved, but no agent association exists",
  // which is a valid steady state, not a failure. hasError means the fetch
  // itself errored and we don't actually know the real state.
  const hasError = !!userQuery.error || !!agent.error;

  const saveSettings = useCallback(
    async (props: Record<string, AgentSettingValue>) => {
      const propNames = Object.keys(props);
      if (!agentEntityId || !agent.object || !user?.id) {
        logger.error(
          'Skipped saving agent setting: missing prerequisite',
          undefined,
          {
            agentEntityId,
            hasAgentObject: !!agent.object,
            hasUserId: !!user?.id,
            orgId: orgIdNum,
            propNames,
          }
        );
        return;
      }
      const entityId = agent.object.entity_id;
      const submitAt = (startSeq: number) => {
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(user.id),
          startSeq,
        });
        builder.propertiesUpdated(entityId, AHOY_AGENT, props);
        return submitEvents({ events: builder.build() });
      };
      try {
        try {
          await submitAt(agent.object.last_event_seq);
        } catch (err) {
          // The agent entity's seq advances under the page on every run
          // (ahoy_agent_ran is written onto it), so a stale last_event_seq is
          // routine rather than a real conflict. The 409 carries the current
          // seq; resubmit once from it before giving up.
          if (
            err instanceof PropertyTypeConflictError &&
            typeof err.lastSeq === 'number'
          ) {
            logger.info('Agent settings seq collision, retrying once', {
              agentEntityId,
              orgId: orgIdNum,
              propNames,
              lastSeq: err.lastSeq,
            });
            await submitAt(err.lastSeq);
          } else {
            throw err;
          }
        }
      } catch (err) {
        logger.error(
          'Failed to save agent setting',
          err instanceof Error ? err : new Error(String(err)),
          { agentEntityId, orgId: orgIdNum, propNames }
        );
        toast.error("We couldn't save your agent settings. Try again.");
        throw err;
      }
    },
    [agentEntityId, agent.object, user, submitEvents, orgIdNum]
  );

  const saveProperty = useCallback(
    (propName: string, propValue: AgentSettingValue) =>
      saveSettings({ [propName]: propValue }),
    [saveSettings]
  );

  // Pause/resume writes ahoy_enabled ONLY. Stored grants survive a pause and
  // are gated server-side, so a resume restores the user's configuration.
  const saveEnabled = useCallback(
    (value: boolean) => saveProperty('ahoy_enabled', value ? 'true' : 'false'),
    [saveProperty]
  );

  // Contacts scope on/off writes ahoy_add_contacts ONLY. The three extras stay
  // stored; AgentSettings.parse resolves them off while the scope is off.
  const saveAddContacts = useCallback(
    (value: boolean) =>
      saveProperty('ahoy_add_contacts', value ? 'true' : 'false'),
    [saveProperty]
  );

  const saveMergeContacts = useCallback(
    (value: boolean) =>
      saveProperty('ahoy_merge_contacts', value ? 'true' : 'false'),
    [saveProperty]
  );

  const saveAssignContacts = useCallback(
    (value: boolean) =>
      saveProperty('ahoy_assign_contacts', value ? 'true' : 'false'),
    [saveProperty]
  );

  const saveEditContacts = useCallback(
    (value: boolean) =>
      saveProperty('ahoy_edit_contacts', value ? 'true' : 'false'),
    [saveProperty]
  );

  // Composite property: the whole row set is written every time, so a
  // toggled grant is an update-to-the-full-array by the caller.
  const savePipelineGrants = useCallback(
    (rows: PipelineGrantRowValue[]) =>
      saveProperty(
        'ahoy_pipeline_grants',
        rows.map(r => ({
          entity_type_name: r.entityTypeName,
          mark_completed: r.markCompleted ? 'true' : 'false',
          move_backward: r.moveBackward ? 'true' : 'false',
          merge_duplicates: r.mergeDuplicates ? 'true' : 'false',
          assign_to_teammates: r.assignToTeammates ? 'true' : 'false',
          edit_entered_details: r.editEnteredDetails ? 'true' : 'false',
        }))
      ),
    [saveProperty]
  );

  useEffect(() => {
    if (userQuery.error) {
      logger.error(
        'Failed to fetch user entity for agent settings',
        userQuery.error,
        {
          userEntityId,
          orgId: orgIdNum,
        }
      );
    }
  }, [userQuery.error, userEntityId, orgIdNum]);

  useEffect(() => {
    if (agent.error) {
      logger.error(
        'Failed to fetch agent entity for agent settings',
        agent.error,
        {
          agentEntityId,
          orgId: orgIdNum,
        }
      );
    }
  }, [agent.error, agentEntityId, orgIdNum]);

  useEffect(() => {
    if (
      userObjectLoaded &&
      agentEntityId === undefined &&
      !userQuery.isLoading &&
      !userQuery.error
    ) {
      logger.info('User entity loaded with no agent association', {
        userEntityId,
        orgId: orgIdNum,
      });
    }
  }, [
    userObjectLoaded,
    agentEntityId,
    userQuery.isLoading,
    userQuery.error,
    userEntityId,
    orgIdNum,
  ]);

  const retry = useCallback(() => {
    if (!agentEntityId) {
      void userQuery.refetch();
    } else {
      void agent.refetch();
    }
  }, [agentEntityId, userQuery, agent]);

  return {
    enabled,
    addContacts,
    mergeContacts,
    assignContacts,
    editContacts,
    pipelineGrants,
    caseTypes,
    isLoading,
    isAbsent,
    hasError,
    // Keep controls disabled through the post-save background refetch that
    // useSubmitEvents's onSuccess triggers, not just the mutation itself —
    // otherwise a save fired right after can submit against a stale
    // last_event_seq before the refetch lands. See the "Await invalidation"
    // pattern in NoteLinkedRecordsSection.tsx for the same hazard elsewhere.
    isSaving: isPending || agent.isFetching,
    saveEnabled,
    saveAddContacts,
    saveMergeContacts,
    saveAssignContacts,
    saveEditContacts,
    savePipelineGrants,
    saveSettings,
    retry,
  };
}
